import {
  AuthForgotPassword_Create,
  AuthLogin_Create,
  AuthMethod_Query,
  AuthResetPassword_Create,
  AuthSignup_Create,
  deleteV1AuthLogin,
  MagicLink_Create,
  MagicLink_Login,
  OtpSms_Create,
  OtpSms_Login,
  postV1AuthForgotPassword,
  postV1AuthLogin,
  postV1AuthMagicLink,
  postV1AuthMagicLinkLogin,
  postV1AuthMethods,
  postV1AuthOtpSms,
  postV1AuthOtpSmsLogin,
  postV1AuthResetPassword,
  postV1AuthSignup,
  postV1AuthSsoLogin,
  SSO_Login,
} from '@treadinc/horizon-api-spec';
import { deleteToken } from 'firebase/messaging';
import { t } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { messaging } from '~notifications/firebase';
import { ROUTES as routes } from '~router/routePaths';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import {
  getSafeRedirectPath,
  SIGN_IN_REDIRECT_PARAM,
  takeRememberedSignInRedirect,
} from '~utils/signInRedirect';

export default function useLogin() {
  const { authStore, toasterStore } = useStores();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const navigateAfterLogin = () => {
    const rememberedPath = takeRememberedSignInRedirect();
    const requestedPath = getSafeRedirectPath(searchParams.get(SIGN_IN_REDIRECT_PARAM));

    navigate(requestedPath ?? rememberedPath ?? routes.base);
  };

  const getAuthMethods = async ({ email }: AuthMethod_Query) => {
    try {
      return await postV1AuthMethods({ body: { email } });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('form_validation_errors.unable_get_auth_methods'),
      );
      throw error;
    }
  };

  const triggerResetPassword = async ({ email }: AuthForgotPassword_Create) => {
    try {
      await postV1AuthForgotPassword({ body: { email } });
      toasterStore.push(alert(t('actions.reset_email_will_be_sent'), AlertTypes.success));
      return email;
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const login = async ({ email, password }: AuthLogin_Create) => {
    try {
      const response = await postV1AuthLogin({ body: { email, password } });
      if (!response.data.data.session_jwt) {
        return;
      }

      AnalyticsController.track('user_signed_in', { auth_method: 'password' });
      authStore.login();
      navigateAfterLogin();
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const loginWithMagicLink = async ({ magic_links_token }: MagicLink_Login) => {
    try {
      const response = await postV1AuthMagicLinkLogin({ body: { magic_links_token } });
      if (!response.data.data.session_jwt) {
        return;
      }

      AnalyticsController.track('user_signed_in', { auth_method: 'magic_link' });
      authStore.login();
      navigateAfterLogin();
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const loginWithSso = async ({ sso_token }: SSO_Login) => {
    try {
      const response = await postV1AuthSsoLogin({ body: { sso_token } });
      if (!response.data.data.session_jwt) {
        return;
      }

      AnalyticsController.track('user_signed_in', { auth_method: 'sso' });
      authStore.login();
      navigateAfterLogin();
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const loginWithOtp = async ({ phone_id, code }: OtpSms_Login) => {
    try {
      const response = await postV1AuthOtpSmsLogin({ body: { phone_id, code } });
      if (!response.data.data.session_jwt) {
        return;
      }

      AnalyticsController.track('user_signed_in', { auth_method: 'otp' });
      authStore.login();
      navigateAfterLogin();
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const resetPassword = async ({
    password,
    password_reset_token,
  }: AuthResetPassword_Create) => {
    try {
      const response = await postV1AuthResetPassword({
        body: {
          password,
          password_reset_token,
        },
      });
      if (!response.data.data.session_jwt) {
        return;
      }

      toasterStore.push(alert(t('actions.password_updated'), AlertTypes.success));
      authStore.login();
      if (authStore.isRedirectToMobileNeeded) {
        navigate(`/${routes.authConfirmation}`);
      } else {
        navigateAfterLogin();
      }
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const sendMagicLink = async ({ email }: MagicLink_Create) => {
    try {
      return await postV1AuthMagicLink({ body: { email } });
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const sendOtpSms = async ({ phone }: OtpSms_Create) => {
    try {
      const response = await postV1AuthOtpSms({ body: { phone } });
      return response.data.data;
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const signUp = async ({ password, magic_links_token }: AuthSignup_Create) => {
    try {
      const response = await postV1AuthSignup({
        body: {
          magic_links_token,
          password,
        },
      });
      if (!response.data.data.session_jwt) {
        return;
      }

      AnalyticsController.track('user_signed_up');
      if (authStore.isRedirectToMobileNeeded) {
        navigate(`/${routes.authConfirmation}`);
      } else {
        authStore.login();
        navigateAfterLogin();
      }
    } catch (error) {
      connection.handleRequestError(error);
      throw error;
    }
  };

  const logOut = async () => {
    try {
      await deleteToken(messaging);
      await deleteV1AuthLogin();
      // send_instantly so the event isn't dropped by the window.location.reload() in finally
      AnalyticsController.track('user_signed_out', undefined, { send_instantly: true });
      AnalyticsController.reset();
      authStore.logout();
      navigate(routes.signIn);
    } catch (error) {
      try {
        connection.handleRequestError(error, t('actions.logout_failed'));
      } catch {
        // Error already reported (toast + Sentry).
      }
    } finally {
      window.location.reload();
    }
  };

  return {
    getAuthMethods,
    login,
    loginWithMagicLink,
    loginWithOtp,
    loginWithSso,
    logOut,
    resetPassword,
    sendMagicLink,
    sendOtpSms,
    signUp,
    triggerResetPassword,
  };
}
